import React, { useCallback, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { KeyRound, Loader2, Mail, RefreshCw, Save } from 'lucide-react';
import EmailSenderProfileManager from './EmailSenderProfileManager';
import { Alert, AlertProps } from '../../../Shared/Utils/Alert';
import {
  DEFAULT_VALIDATION_POLICY,
  getPasswordRuleChecks,
  invalidateValidationPolicy,
  ValidationPolicy,
} from '../../../Shared/Utils/validationPolicy';
import { getSystemConfiguration, updateSystemConfiguration } from '../../Services/AdminService';

export type SystemConfigurationMode = 'email' | 'password';

const MODE_META = {
  email: {
    configKey: 'system.emailFormatValidationEnabled',
    title: 'Email Config',
    subtitle: 'Email format validation rules',
    toggleLabel: 'Enable Email Format Validation',
    toggleBlurb: 'Rejects malformed email addresses everywhere the portal accepts one.',
  },
  password: {
    configKey: 'system.passwordPolicyValidationEnabled',
    title: 'Password Config',
    subtitle: 'Password policy validation rules',
    toggleLabel: 'Enable Password Policy Validation',
    toggleBlurb: 'Enforces the rules below on every password the portal sets or changes.',
  },
} as const;

interface Props {
  mode: SystemConfigurationMode;
}

export function SystemConfigurationDetail({ mode }: Props) {
  const meta = MODE_META[mode];
  const flagKey =
    mode === 'email' ? 'emailFormatValidationEnabled' : 'passwordPolicyValidationEnabled';

  const [config, setConfig] = useState<ValidationPolicy>(DEFAULT_VALIDATION_POLICY);
  const [original, setOriginal] = useState<ValidationPolicy>(DEFAULT_VALIDATION_POLICY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState<AlertProps | null>(null);

  const showAlert = (variant: AlertProps['variant'], heading: string, message: string) =>
    setAlert({ variant, title: heading, message, autoCloseMs: 5000, onClose: () => setAlert(null) });

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getSystemConfiguration();
      if (response.success && response.data) {
        const next = { ...DEFAULT_VALIDATION_POLICY, ...response.data };
        setConfig(next);
        setOriginal(next);
      } else {
        showAlert('error', 'Load Error', response.message || 'Failed to load configuration.');
      }
    } catch {
      showAlert('error', 'Load Error', 'Failed to load configuration.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const dirty = config[flagKey] !== original[flagKey];

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await updateSystemConfiguration(meta.configKey, config[flagKey]);
      if (response.success && response.data) {
        const next = { ...DEFAULT_VALIDATION_POLICY, ...response.data };
        setConfig(next);
        setOriginal(next);
        invalidateValidationPolicy();
        showAlert('success', 'Saved', `${meta.title} updated.`);
      } else {
        showAlert('error', 'Save Error', response.message || 'Failed to save configuration.');
      }
    } catch {
      showAlert('error', 'Save Error', 'Failed to save configuration.');
    } finally {
      setSaving(false);
    }
  };

  const ruleChecks = getPasswordRuleChecks('', config);
  const ModeIcon = mode === 'email' ? Mail : KeyRound;

  return (
    <div className="min-h-screen bg-slate-950 p-4 text-slate-100 md:p-8">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl border border-cyan-500/30 bg-cyan-500/10 text-cyan-400">
            <ModeIcon size={22} />
          </span>
          <div>
            <h1 className="text-xl font-black tracking-tight text-slate-100 md:text-2xl">{meta.title}</h1>
            <p className="text-xs font-semibold text-slate-400 md:text-sm">{meta.subtitle}</p>
          </div>
        </div>
        <div className="flex gap-2 sm:ml-auto">
          <button
            onClick={fetchConfig}
            title="Refresh"
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-800 bg-slate-900 px-4 py-2.5 text-sm font-bold text-slate-200 transition hover:border-cyan-500/40 hover:text-cyan-400 disabled:opacity-60"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
          <button
            onClick={handleSave}
            disabled={saving || loading || !dirty}
            className="inline-flex items-center gap-2 rounded-xl bg-cyan-500 px-4 py-2.5 text-sm font-black text-slate-950 transition hover:bg-cyan-400 disabled:opacity-50"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            Save Changes
          </button>
        </div>
      </div>

      {alert && (
        <div className="mb-4">
          <Alert {...alert} onClose={() => setAlert(null)} />
        </div>
      )}

      {loading ? (
        <div className="flex min-h-[40vh] items-center justify-center text-slate-500">
          <Loader2 className="h-6 w-6 animate-spin text-cyan-500" />
        </div>
      ) : (
        <div className="max-w-3xl space-y-6">
          <section className="rounded-2xl border border-slate-800 bg-slate-900/50 p-5">
            <label className="flex items-start gap-3 rounded-2xl border border-slate-800 bg-slate-950/50 px-4 py-3 transition hover:border-cyan-500/30">
              <input
                type="checkbox"
                checked={config[flagKey]}
                onChange={(e) => setConfig((prev) => ({ ...prev, [flagKey]: e.target.checked }))}
                className="mt-0.5 h-4 w-4 shrink-0 accent-cyan-500"
              />
              <span className="min-w-0">
                <span className="block text-sm font-bold text-slate-100">{meta.toggleLabel}</span>
                <span className="block text-[11px] font-semibold text-slate-500">{meta.toggleBlurb}</span>
              </span>
            </label>

            <div className="mt-5 space-y-4">
              <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/5 px-4 py-3">
                <p className="text-[10px] font-black uppercase tracking-widest text-emerald-400">
                  When enabled (default)
                </p>
                <ul className="mt-2 space-y-1.5 text-[11px] font-semibold text-slate-400">
                  {mode === 'email' ? (
                    <>
                      <li>· Registration, invitations and early access only accept valid email addresses.</li>
                      <li>· Contact/feedback submissions and forgot-password reject unusable addresses.</li>
                      <li>· Profile email updates are checked before saving.</li>
                    </>
                  ) : (
                    <>
                      <li>· Registration, invites, reset and change-password enforce the rules.</li>
                      <li>· Password fields show a live checklist before submitting.</li>
                    </>
                  )}
                </ul>
              </div>
              <div className="rounded-2xl border border-amber-500/20 bg-amber-500/5 px-4 py-3">
                <p className="text-[10px] font-black uppercase tracking-widest text-amber-400">
                  When disabled (testing only)
                </p>
                <ul className="mt-2 space-y-1.5 text-[11px] font-semibold text-slate-400">
                  {mode === 'email' ? (
                    <>
                      <li>· Any string is accepted where an email is expected.</li>
                      <li>· Addresses are never format-checked before sending.</li>
                      <li>· Intended for local/testing environments only.</li>
                    </>
                  ) : (
                    <>
                      <li>· Any non-empty password is accepted.</li>
                      <li>· The live checklist is hidden on password forms.</li>
                    </>
                  )}
                </ul>
              </div>
            </div>
          </section>

          {mode === 'email' && <EmailSenderProfileManager />}

          {mode === 'password' && (
            <section className="rounded-2xl border border-slate-800 bg-slate-900/50 p-5">
              <header className="mb-3">
                <h2 className="text-sm font-black uppercase tracking-widest text-slate-200">Enforced rules</h2>
              </header>
              <ul className="space-y-1.5">
                {ruleChecks.map((check) => (
                  <li key={check.key} className="text-[11px] font-semibold text-slate-400">
                    · {check.label}
                  </li>
                ))}
                <li className="text-[11px] font-semibold text-slate-400">· Empty passwords are always rejected</li>
              </ul>
              <p className="mt-3 font-mono text-xs font-bold text-emerald-400">Example: Nexgent@2026</p>
            </section>
          )}
        </div>
      )}

      <AnimatePresence>
        {dirty && !loading && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2"
          >
            <button
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-full border border-cyan-500/40 bg-slate-900 px-5 py-3 text-xs font-black uppercase tracking-widest text-cyan-400 shadow-[0_0_30px_-8px_rgba(34,211,238,0.5)] transition hover:bg-cyan-500/10 disabled:opacity-60"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Unsaved changes · Save
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default SystemConfigurationDetail;
